import React, { useState, useCallback } from "react";
import ReactModal from "react-modal";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import NumberFormat from "react-number-format";
import { useAuth0 } from "@auth0/auth0-react";
import { useDebouncedCallback } from "use-debounce";
import { LoadingButton } from "../shared/loading-button";
import ai from "../../helpers/axios";
import {
  formatTripOption,
  formatContractOption,
  formatGroupedOptionsByStage,
} from "../payments-linking/helpers";

const CREDIT_CATEGORIES = [
  "Refund - Partial Trip",
  "Refund - Canceled Trip",
  "Refund - Overpayment",
  "Funds on File (General)",
  "Membership Credits",
].map((v) => ({ label: v, value: v }));

const DEBIT_CATEGORIES = [
  "Flight Charges",
  "Catering",
  "Ground Transportation",
  "Other Incidentals",
].map((v) => ({ label: v, value: v }));

const PAYMENT_TYPES = [
  "Funds on File (General)",
  "Funds on File (Membership)",
  "Membership Credits",
].map((v) => ({ label: v, value: v }));

const EMPTY_FORM = {
  targetTrip: null,
  targetContract: null,
  paymentType: null,
  amount: "",
  creditCategory: null,
  debitCategory: null,
  notes: "",
};

const buildNotesDefault = (sourceTrip, sourceContract, targetTrip, targetContract) => {
  if (!sourceTrip || !sourceContract || !targetTrip || !targetContract) return "";
  return `Move funds from the Trip #${sourceTrip.tripTag} | Contract #${sourceContract.contractTag} to the Trip #${targetTrip.tripTag} | Contract #${targetContract.contractTag}`;
};

const getTrips = async (token, params = {}) => {
  const { data } = await ai.auth(token).get("/api/trips/search", { params });
  return { data: data?.data ?? [] };
};

const getContracts = async (token, params = {}) => {
  const { data } = await ai.auth(token).get("/api/trips/contracts/search", { params });
  return { data: data?.data ?? [] };
};

const MoveFundsModal = ({ open, sourceTrip, sourceContract, onSubmit, onClose, saving }) => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  const getToken = useCallback(
    async () => getAccessTokenSilently().catch(() => loginWithRedirect()),
    [getAccessTokenSilently, loginWithRedirect],
  );

  const prospectId = sourceContract?.clientId;

  const loadTrips = useCallback(async (inputValue) => {
    const token = await getToken();
    const { data: trips } = await getTrips(token, {
      q: inputValue,
      prospectFilter: prospectId,
      limit: 50,
    });
    return trips.map(formatTripOption);
  }, [getToken, prospectId]);

  const debouncedLoadTrips = useDebouncedCallback(
    (inputValue, callback) => {
      loadTrips(inputValue).then(callback).catch(() => callback([]));
    },
    500,
  );

  const loadContracts = useCallback(async (inputValue) => {
    const token = await getToken();
    const { data: contracts } = await getContracts(token, {
      q: inputValue,
      prospectFilter: prospectId,
      tripFilter: form.targetTrip?.value,
      limit: 50,
    });
    return formatGroupedOptionsByStage(contracts, formatContractOption, !form.targetTrip);
  }, [getToken, prospectId, form.targetTrip]);

  const debouncedLoadContracts = useDebouncedCallback(
    (inputValue, callback) => {
      loadContracts(inputValue).then(callback).catch(() => callback([]));
    },
    500,
  );

  const handleTargetTripChange = (selected) => {
    setForm((f) => {
      const updatedNotes = buildNotesDefault(
        sourceTrip,
        sourceContract,
        selected ? { tripTag: selected.label.split(" | ")[0] } : null,
        f.targetContract ? { contractTag: f.targetContract.label.split(" | ")[0] } : null,
      );
      return { ...f, targetTrip: selected, targetContract: null, notes: updatedNotes };
    });
  };

  const handleTargetContractChange = (selected) => {
    setForm((f) => {
      const updatedNotes = buildNotesDefault(
        sourceTrip,
        sourceContract,
        f.targetTrip ? { tripTag: f.targetTrip.label.split(" | ")[0] } : null,
        selected ? { contractTag: selected.label.split(" | ")[0] } : null,
      );
      return { ...f, targetContract: selected, notes: updatedNotes };
    });
  };

  const notesRequired = form.debitCategory?.value === "Other Incidentals";

  const validate = () => {
    const e = {};
    if (!form.targetTrip) e.targetTrip = "Required";
    if (!form.targetContract) e.targetContract = "Required";
    if (!form.paymentType) e.paymentType = "Required";
    if (!form.amount) e.amount = "Required";
    if (!form.creditCategory) e.creditCategory = "Required";
    if (!form.debitCategory) e.debitCategory = "Required";
    if (notesRequired && !form.notes.trim()) e.notes = "Required for Other Incidentals";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleClose = () => {
    setForm(EMPTY_FORM);
    setErrors({});
    onClose();
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    await onSubmit({
      sourceContractId: sourceContract._id,
      targetTripId: form.targetTrip.value,
      targetContractId: form.targetContract.value,
      paymentType: form.paymentType.value,
      amount: parseFloat(form.amount),
      creditCategory: form.creditCategory.value,
      debitCategory: form.debitCategory.value,
      notes: form.notes,
    });
    setForm(EMPTY_FORM);
    setErrors({});
  };

  return (
    <ReactModal
      isOpen={open}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
      onRequestClose={handleClose}
      style={{
        overlay: {
          zIndex: 999999,
          backgroundColor: "rgb(32 53 68 / 50%)",
        },
        content: {
          width: "auto",
          border: "none",
          overflowY: "scroll",
          maxHeight: "100vh",
          minWidth: "35vw",
          borderRadius: "0",
          boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
        },
      }}
      appElement={document.getElementById("app")}
    >
      <div className="modal-header base-modal">
        <h4>Move Funds to another Trip/Contract</h4>
      </div>

      <div className="modal-body base-modal overflow-scroll">
        <form className="w-100 base-form mt-20" onSubmit={handleSubmit}>
          {sourceContract && (
            <p className="mb-3 text-muted">
              From: <strong>{sourceTrip?.tripTag}</strong> | Contract <strong>{sourceContract.contractTag}</strong>
            </p>
          )}

          <div className="form-group mb-3">
            <label className="base-label">Trip # <span className="text-danger">*</span></label>
            <AsyncSelect
              loadOptions={debouncedLoadTrips}
              defaultOptions
              value={form.targetTrip}
              onChange={handleTargetTripChange}
              placeholder="Search trips..."
              isClearable
              classNamePrefix="select"
            />
            {errors.targetTrip && <small className="text-danger">{errors.targetTrip}</small>}
          </div>

          <div className="form-group mb-3">
            <label className="base-label">Contract # <span className="text-danger">*</span></label>
            <AsyncSelect
              key={form.targetTrip?.value || "no-trip"}
              loadOptions={debouncedLoadContracts}
              defaultOptions={!!form.targetTrip}
              value={form.targetContract}
              onChange={handleTargetContractChange}
              placeholder="Search contracts..."
              isClearable
              isDisabled={!form.targetTrip}
              classNamePrefix="select"
            />
            {errors.targetContract && <small className="text-danger">{errors.targetContract}</small>}
          </div>

          <div className="form-group mb-3">
            <label className="base-label">Payment Type (Source) <span className="text-danger">*</span></label>
            <Select
              options={PAYMENT_TYPES}
              value={form.paymentType}
              onChange={(v) => setForm((f) => ({ ...f, paymentType: v }))}
              placeholder="Select..."
              classNamePrefix="select"
            />
            {errors.paymentType && <small className="text-danger">{errors.paymentType}</small>}
          </div>

          <div className="form-group mb-3">
            <label className="base-label">Amount <span className="text-danger">*</span></label>
            <NumberFormat
              thousandSeparator
              prefix="$"
              decimalScale={2}
              fixedDecimalScale
              allowNegative={false}
              value={form.amount}
              onValueChange={({ floatValue }) =>
                setForm((f) => ({ ...f, amount: floatValue ?? "" }))
              }
              placeholder="$0.00"
              classNamePrefix="select"
            />
            {errors.amount && <small className="text-danger">{errors.amount}</small>}
          </div>

          <div className="form-group mb-3">
            <label className="base-label">Ledger Credit Category <span className="text-danger">*</span></label>
            <Select
              options={CREDIT_CATEGORIES}
              value={form.creditCategory}
              onChange={(v) => setForm((f) => ({ ...f, creditCategory: v }))}
              placeholder="Select..."
              classNamePrefix="select"
            />
            {errors.creditCategory && <small className="text-danger">{errors.creditCategory}</small>}
          </div>

          <div className="form-group mb-3">
            <label className="base-label">Ledger Debit Category <span className="text-danger">*</span></label>
            <Select
              options={DEBIT_CATEGORIES}
              value={form.debitCategory}
              onChange={(v) => setForm((f) => ({ ...f, debitCategory: v }))}
              placeholder="Select..."
              classNamePrefix="select"
            />
            {errors.debitCategory && <small className="text-danger">{errors.debitCategory}</small>}
          </div>

          <div className="form-group mb-3">
            <label className="base-label">Notes {notesRequired && <span className="text-danger">*</span>}</label>
            <textarea
              className="form-control"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
            {errors.notes && <small className="text-danger">{errors.notes}</small>}
          </div>
        </form>
      </div>

      <div className="modal-footer no-border w-100 d-flex gap-10">
        <button className="base-button btn-danger flex-half" disabled={saving} onClick={handleClose} type="button">
          Cancel
        </button>

        <LoadingButton
          className="base-button evo-blue flex-half"
          onClick={handleSubmit}
          isLoading={saving}
          type="button"
        >
          Submit
        </LoadingButton>
      </div>
    </ReactModal>
  );
};

export default MoveFundsModal;
